import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  UserGroupIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // Set up axios defaults on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await axios.get('/api/customers', { params });
      setCustomers(response.data.customers);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewCustomer = async (customerId) => {
    try {
      const response = await axios.get(`/api/customers/${customerId}`);
      const customerData = response.data;

      // The Services tab must use calculation internally since all endpoints return $500
      // Use the original customer data - the totals and addons are correct
      setSelectedCustomer(customerData);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching customer details:', err);
      alert('Failed to load customer details');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPhone = (phone) => {
    // Simple phone formatting
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && customers.length === 0) {
    return (
      <EmptyState
        icon={UserGroupIcon}
        title="Error Loading Customers"
        description={error}
        action={{
          label: 'Retry',
          onClick: fetchCustomers
        }}
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="card">
        <div className="card-header">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Customers</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage customer information and view rental history</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, phone, or email..."
                  className="input pl-10"
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary w-full sm:w-auto"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Customers List */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-base sm:text-lg font-medium text-gray-900">
            Customers ({pagination.total})
          </h2>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
          </div>
        ) : customers.length === 0 ? (
          <div className="card-body">
            <EmptyState
              icon={UserGroupIcon}
              title="No Customers Found"
              description={searchTerm ? `No customers found matching "${searchTerm}"` : 'No customers have been added yet.'}
            />
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reservations
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {customer.firstName.charAt(0)}{customer.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.firstName} {customer.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {formatPhone(customer.phone)}
                        </div>
                        {customer.email && (
                          <div className="flex items-center mt-1">
                            <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="text-gray-500">{customer.email}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {customer.address ? (
                          <div className="flex items-start">
                            <MapPinIcon className="h-4 w-4 mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>{customer.address}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {customer._count?.reservations || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleViewCustomer(customer.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4 p-4">
            {customers.map((customer) => (
              <div key={customer.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="space-y-3">
                  {/* Customer Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-base font-medium text-gray-700">
                          {customer.firstName.charAt(0)}{customer.lastName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-base font-semibold text-gray-900">
                          {customer.firstName} {customer.lastName}
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                          {customer._count?.reservations || 0} reservations
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex items-center text-sm">
                      <PhoneIcon className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-900">{formatPhone(customer.phone)}</span>
                    </div>
                    {customer.email && (
                      <div className="flex items-center text-sm">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 truncate">{customer.email}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex items-start text-sm">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{customer.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="pt-3 border-t">
                    <button
                      onClick={() => handleViewCustomer(customer.id)}
                      className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>View Details</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
              Page {pagination.page} of {pagination.pages}
              <span className="hidden sm:inline"> ({pagination.total} total)</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Customer Details</h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-medium text-gray-700">
                      {selectedCustomer.firstName.charAt(0)}{selectedCustomer.lastName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </h3>
                    <p className="text-gray-600">Customer since {formatDate(selectedCustomer.createdAt)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">{formatPhone(selectedCustomer.phone)}</span>
                  </div>
                  {selectedCustomer.email && (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-900">{selectedCustomer.email}</span>
                    </div>
                  )}
                  {selectedCustomer.address && (
                    <div className="flex items-center md:col-span-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-900">{selectedCustomer.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Reservation History */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reservation History</h3>
                {selectedCustomer.reservations && selectedCustomer.reservations.length > 0 ? (
                  <div className="space-y-4">
                    {selectedCustomer.reservations.map((reservation) => (
                      <div key={reservation.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {reservation.reservationNumber}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              reservation.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                              reservation.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {reservation.status}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatDate(reservation.createdAt)}
                          </span>
                        </div>
                        
                        <div className="mb-4">
                          <div>
                            <p className="text-gray-500 text-sm">Item/Service</p>
                            <p className="font-medium">
                              {reservation.golfCart
                                ? reservation.golfCart.cartNumber
                                : reservation.inventoryItem
                                  ? `${reservation.inventoryItem.itemNumber} (${reservation.inventoryItem.productType?.name || 'Item'})`
                                  : reservation.service
                                    ? reservation.service.name
                                    : 'N/A'
                              }
                            </p>
                          </div>
                        </div>

                        {/* Financial Information - Fetch from Service Reservation Data */}
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-3">Financial Information</h4>

                          {reservation.service ? (
                            // Use the Services tab data to display the full breakdown (same as ReservationDetailsModal)
                            (() => {
                              const participantMatch = reservation.notes?.match(/Participants:\s*(\d+)/i);
                              const participants = participantMatch ? parseInt(participantMatch[1]) : 1;

                              // Use stored booking-time rates when available, fallback to calculation for older reservations
                              const storedRate = reservation.serviceRateAtBooking;
                              const storedRateType = reservation.serviceRateTypeAtBooking;

                              let rate, rateType, serviceAmount;

                              if (storedRate !== null && storedRate !== undefined && storedRateType) {
                                // Use historical booking rates for accurate financial display
                                rate = storedRate;
                                rateType = storedRateType;

                                // Calculate service amount based on stored rate and participants
                                if (rateType === 'PER_PERSON') {
                                  serviceAmount = rate * participants;
                                } else {
                                  serviceAmount = rate;
                                }
                              } else {
                                // Fallback to reverse calculation for older reservations without stored rates
                                const addonTotal = (reservation.addons || []).reduce((sum, addon) => sum + (parseFloat(addon.totalAmount) || 0), 0);
                                serviceAmount = (reservation.totalAmount || 0) - addonTotal;
                                rate = serviceAmount;
                                rateType = reservation.service.rateType;
                              }

                              // Use add-ons from the Services tab data
                              let selectedAddons = [];
                              if (reservation.addons && reservation.addons.length > 0) {
                                selectedAddons = reservation.addons.map(addon => ({
                                  name: addon.addonName,
                                  rate: parseFloat(addon.addonRate) || 0,
                                  rateType: addon.rateType || 'PER_PERSON',
                                  quantity: addon.quantity || 1,
                                  participants: participants,
                                  total: parseFloat(addon.totalAmount) || 0
                                }));
                              }

                              const totalSubtotal = serviceAmount + selectedAddons.reduce((sum, addon) => sum + addon.total, 0);

                              return (
                                <div className="space-y-4">
                                  {/* Desktop Table View */}
                                  <div className="hidden md:block overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {selectedAddons.some(addon => addon.rateType === 'PER_ITEM') ? 'Quantity' : '# Participants'}
                                          </th>
                                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {/* Service Row */}
                                        <tr>
                                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {reservation.service.name || 'Event Ticket'}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                            {participants}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                            {rateType === 'PER_PERSON' ? `$${rate.toFixed(2)}` :
                                             rateType === 'HOURLY' ? `$${rate.toFixed(2)}/hour` :
                                             `$${rate.toFixed(2)}`}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                            ${serviceAmount.toFixed(2)}
                                          </td>
                                        </tr>

                                        {/* Add-on Rows */}
                                        {selectedAddons.map((addon, index) => (
                                          <tr key={index}>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                              {addon.name}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                              {addon.rateType === 'PER_PERSON' ? addon.participants : addon.quantity}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                              ${addon.rate.toFixed(2)}{addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                              ${addon.total.toFixed(2)}
                                            </td>
                                          </tr>
                                        ))}

                                        {/* Subtotal Row */}
                                        <tr className="bg-gray-50">
                                          <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                            Subtotal:
                                          </td>
                                          <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            ${totalSubtotal.toFixed(2)}
                                          </td>
                                        </tr>

                                        {/* Final Total Row */}
                                        <tr className="bg-gray-100 border-t-2">
                                          <td colSpan="3" className="px-4 py-3 text-lg font-bold text-gray-900 text-right">
                                            Final Total:
                                          </td>
                                          <td className="px-4 py-3 text-lg font-bold text-gray-900 text-right">
                                            ${totalSubtotal.toFixed(2)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Mobile Card View */}
                                  <div className="md:hidden space-y-3">
                                    {/* Service Card */}
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                          <h5 className="font-medium text-gray-900">{reservation.service.name || 'Event Ticket'}</h5>
                                          <p className="text-sm text-gray-600">{participants} participants</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm text-gray-600">
                                            {rateType === 'PER_PERSON' ? `$${rate.toFixed(2)}` :
                                             rateType === 'HOURLY' ? `$${rate.toFixed(2)}/hour` :
                                             `$${rate.toFixed(2)}`}
                                          </p>
                                          <p className="font-medium text-gray-900">${serviceAmount.toFixed(2)}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Add-on Cards */}
                                    {selectedAddons.map((addon, index) => (
                                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <h5 className="font-medium text-gray-900">{addon.name}</h5>
                                            <p className="text-sm text-gray-600">
                                              {addon.rateType === 'PER_PERSON' ? `${addon.participants} participants` : `${addon.quantity} items`}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm text-gray-600">
                                              ${addon.rate.toFixed(2)}{addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                                            </p>
                                            <p className="font-medium text-gray-900">${addon.total.toFixed(2)}</p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}

                                    {/* Total Summary */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <div className="flex justify-between items-center">
                                        <h5 className="text-lg font-bold text-gray-900">Final Total:</h5>
                                        <p className="text-lg font-bold text-gray-900">${totalSubtotal.toFixed(2)}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            // Non-service reservations (fallback)
                            <div className="text-sm text-gray-500">
                              <p>Total Amount: ${reservation.totalAmount?.toFixed(2) || '0.00'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No reservation history found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;